import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Utility } from 'helpers/UtilityHelpers';
import { Code } from 'constants/codes';
import apiLogin from "services/loginInterceptors";
import { Location } from 'models/LocationModel';
import ReCAPTCHA from 'react-google-recaptcha';

import 'assets/styles/Intake.css';
import {
  type IntakeOnlineData,
  type IntakeFormErrors,
  type IntakeJsonErrors,
  type YesNoType,
  type YesNoNaType,
  CANADIAN_PROVINCES,
  CORRECTION_TYPE_OPTIONS,
  DIABETIC_TYPE_OPTIONS,
  FAMILY_EYE_CONDITIONS,
  GENDER_OPTIONS,
  INITIAL_FORM_STATE,
  LENS_WEAR_DURATION_OPTIONS,
  MEDICAL_CONDITIONS,
  RELATIONSHIP_OPTIONS,
  VISION_SYMPTOMS,
  toggleArrayItem,
  validateForm,
  IntakeJsonData,
  INITIAL_JSON_STATE,
} from 'models/IntakeOnlineData';

// ─── Shared sub-components ────────────────────────────────────────────────────

interface YesNoRadioProps {
  value: string;
  onChange: (v: string) => void;
  includeNA?: boolean;
  hasError?: boolean;
}
const YesNoRadio: React.FC<YesNoRadioProps> = ({ value, onChange, includeNA = false, hasError }) => {
  const opts = includeNA ? ['Yes', 'No', 'N/A'] : ['Yes', 'No'];
  return (
    <div className="radio-group">
      {opts.map(opt => {
        const isSelected = value === opt;
        const colorClass = isSelected
          ? opt === 'Yes' ? 'selected-yes' : 'selected-no'
          : hasError ? 'error-radio' : '';
        return (
          <div key={opt} className={`radio-option ${colorClass}`} onClick={() => onChange(opt)}>
            <div className="radio-dot">
              {isSelected && <div className="radio-dot-inner" />}
            </div>
            {opt}
          </div>
        );
      })}
    </div>
  );
};

interface CheckboxGroupProps {
  options: string[];
  selected: string[];
  onToggle: (item: string) => void;
}
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ options, selected, onToggle }) => (
  <div className="checkbox-group">
    {options.map(opt => {
      const isChecked = selected.includes(opt);
      return (
        <div key={opt} className={`checkbox-option ${isChecked ? 'checked' : ''}`} onClick={() => onToggle(opt)}>
          <div className="custom-checkbox">
            {isChecked && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
          {opt}
        </div>
      );
    })}
  </div>
);

interface ClinicHeaderProps { name: string; address: string; }
const ClinicHeader: React.FC<ClinicHeaderProps> = ({ name, address }) => (
  <div className="clinic-header">
    <div className="clinic-logo">
      <div className="clinic-name">{name}</div>
    </div>
    <div className="clinic-tagline">{address}</div>
  </div>
);

interface SuccessPanelProps { firstName: string; }
const SuccessPanel: React.FC<SuccessPanelProps> = ({ firstName }) => (
  <div className="success-panel">
    <div className="success-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>Intake Form Submitted!</h3>
    <p>
      Thank you, {firstName}. Your intake form has been received.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const IntakeOnline: React.FC = () => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId]     = useState<string>(paramKeyId ?? '');
  const [toast, setToast] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<Number>(2);
  const [locationId, setLocationId] = useState<Number>(2);
  const [clinicName, setClinicName] = useState<string>('');
  const [clinicAddress, setClinicAddress] = useState<string>('');
  const [locationPath, setLocationPath] = useState<string>(keyId);
  
  // const companyId   = 12;
  // const locationId  = 14;
  // const clinicName    = 'Dr. Azenda Optometry (Walmart Vision)';
  // const clinicAddress = '#3800 Memorial Drive NE, Calgary, AB T2A 2K2';

  const [form, setForm] = useState<IntakeOnlineData>(INITIAL_FORM_STATE);
  const [jsonData, setJsonData] = useState<IntakeJsonData>(INITIAL_JSON_STATE);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<IntakeFormErrors>({});
  const [errJson, setErrJson] = useState<IntakeJsonErrors>({});
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const yearOptions = Array.from({ length: 2026 - 1920 + 1 }, (_, i) => (2026 - i).toString());
  const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  const [dobYear, setDobYear] = useState<string>('');
  const [dobMonth, setDobMonth] = useState<string>('');
  const [dobDay, setDobDay] = useState<string>('');

  const update = <K extends keyof IntakeOnlineData>(key: K, value: IntakeOnlineData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const jsonUpdate = <K extends keyof IntakeJsonData>(key: K, value: IntakeJsonData[K]) =>
    setJsonData(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      intake_dob: dobYear && dobMonth && dobDay
        ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')} 10:00:00`
        : ''
    }));
  }, [dobYear, dobMonth, dobDay]);

  const toggleCheck = (
    key: 'D25' | 'D26' | 'D29',
    item: string,
  ) => setJsonData(prev => ({ ...prev, [key]: toggleArrayItem(prev[key], item) }));

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
  ev.preventDefault();
  const [validationErrors,validationJson] = validateForm(form, jsonData);
  setErrors(validationErrors);
  setErrJson(validationJson);

  if (Object.keys(validationErrors).length > 0 || Object.keys(validationJson).length > 0) {
    setToast('Please fill in all required fields before submitting.');
    setTimeout(() => setToast(null), 4000);
    return;
  }

  if (!recaptchaToken) {
    setToast('Please complete the reCAPTCHA verification.');
    setTimeout(() => setToast(null), 4000);
    return;
  }

  try {
    const response = await fetch(`${Code.Path.SERVER}intake/online`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ...form,
        company_id: companyId,
        location_id: locationId,
        intake_date: Utility.Date.getLocalDate(),
        intake_data: JSON.stringify(jsonData, (key, value) => {
          if (value === null || value === undefined || value === "") return undefined;
          if (value === false || value === 0) return undefined;
          if (Array.isArray(value) && value.length === 0) return undefined;
          if (Array.isArray(value)) return value.join(", ");
          if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return undefined;
          return value;
        }),
        keyId 
      }),
    });

    // if (!response.ok) {
    //   const errorData = await response.json().catch(() => null);
    //   console.error('Submission failed:', response.status, errorData);
    //   // Optionally surface an error to the user here
    //   return;
    // }

    setSubmitted(true);
  } catch (err) {
    console.error('Network error during submission:', err);
    // Optionally surface a network error to the user here
  }
};

 useEffect(() => {
  const fetchLocation = async () => {
    if (locationPath) {
      try {
        const url = `${Code.Path.SERVER}auth/location/${locationPath}`;
        const response = await apiLogin.get(url);
        console.clear();
        //console.log("response>>>>>", response);
        if (response) {
          const result = response.data;
          //console.log("result>>>>>", result.data);
          if (result.status === "success" && result.code === 200) {
            const location = result.data as Location;
            if (location) {
              setCompanyId(location.company_id || 0);
              setLocationId(location.location_id || 0);
              setClinicName(location.company_legal_name || '');
              setClinicAddress(location.location_address?.replace(/\r?\n/g, "") || '');
            }
          }
        }
      } catch (error) {
        console.error("fetchLocation error >>>>>", error);
        setCompanyId(11); //demo xyz optiical
        setLocationId(13); //demo xyz optiical
        setClinicName(Utility.Convert.TitleCase(locationPath?.replaceAll("-"," ") || ''));
        setClinicAddress('');

        
      }
    }
  };

  fetchLocation();
}, [locationPath]);


  useEffect(() => {
    if (paramKeyId) setKeyId(paramKeyId);
  }, [paramKeyId]);

  return (
    <div className="page-wrapper">
      <ClinicHeader name={clinicName} address={clinicAddress} />

      <div className="form-card">
        <div className="form-title-bar">
          <h2>Patient Intake Form</h2>
          <span className="badge">Confidential</span>
        </div>

        <div className="form-body">
          {submitted ? (
            <SuccessPanel firstName={form.intake_first_name} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              {/* ══ 1. Personal Information ══════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Personal Information</div>

                {/* Smoker */}
                <div className="field mb-14">
                  <label>Are you an existing patient? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={jsonData.D79}
                    onChange={v => jsonUpdate('D79', v as YesNoType)}
                    hasError={!!errJson.D79}
                  />
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>First Name <span className="clinic-req">*</span></label>
                    <input className={errors.intake_first_name ? 'error' : ''} placeholder="Jane"
                      value={form.intake_first_name} onChange={e => update('intake_first_name', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Last Name <span className="clinic-req">*</span></label>
                    <input className={errors.intake_last_name ? 'error' : ''} placeholder="Smith"
                      value={form.intake_last_name} onChange={e => update('intake_last_name', e.target.value)} />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Date of Birth <span className="clinic-req">*</span></label>
                    <div className={`dob-selects ${errors.intake_dob ? 'error' : ''}`}>
                      <div className="grid-3">
                        <div className="field">
                          <select value={dobYear} onChange={e => setDobYear(e.target.value)}>
                            <option value="">Year</option>
                            {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                          </select>
                        </div>
                        <div className="field">
                          <select value={dobMonth} onChange={e => setDobMonth(e.target.value)}>
                            <option value="">Month</option>
                            {monthOptions.map(month => <option key={month} value={month}>{month}</option>)}
                          </select>
                        </div>
                        <div className="field">
                          <select value={dobDay} onChange={e => setDobDay(e.target.value)}>
                            <option value="">Day</option>
                            {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="field">
                    <label>Mobile Number <span className="clinic-req">*</span></label>
                    <input type="tel" className={errors.intake_phone ? 'error' : ''} placeholder="(555) 000-0000"
                      value={form.intake_phone} onChange={e => update('intake_phone', e.target.value)} />
                  </div>
                </div>

                <div className="field mb-14">
                  <label>Email Address</label>
                  <input type="email" placeholder="jane.smith@email.com"
                    value={form.intake_email} onChange={e => update('intake_email', e.target.value)} />
                </div>

                {/* Gender */}
                <div className="field">
                  <label>Gender</label>
                  <div className="gender-group">
                    {GENDER_OPTIONS.map(opt => (
                      <div
                        key={opt}
                        className={`gender-pill ${form.intake_gender === opt ? 'selected' : ''}`}
                        onClick={() => update('intake_gender', form.intake_gender === opt ? '' : opt)}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ══ 2. Health Card ═══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Health Card</div>
                <div className="field">
                  <label>Health Card Number</label>
                  <input placeholder="e.g. 1234-567-890"
                    value={form.intake_health_card} onChange={e => update('intake_health_card', e.target.value)} />
                </div>
              </div>

              {/* ══ 3. Home Address ══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Home Address</div>

                <div className="field mb-14">
                  <label>Street Address</label>
                  <input placeholder="123 Maple Street, Apt 4B"
                    value={form.intake_address} onChange={e => update('intake_address', e.target.value)} />
                </div>

                <div className="grid-3">
                  <div className="field col-span-2">
                    <label>City</label>
                    <input placeholder="Calgary"
                      value={form.intake_city} onChange={e => update('intake_city', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Province</label>
                    <select value={form.intake_province} onChange={e => update('intake_province', e.target.value)}>
                      <option value="">—</option>
                      {CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field mt-14 mw-160">
                  <label>Postal Code</label>
                  <input placeholder="T2A 1B3"
                    value={form.intake_postal} onChange={e => update('intake_postal', e.target.value)} />
                </div>
              </div>

              {/* ══ 4. Emergency Contact ═════════════════════════════════════════ */}
              <div className="section section--emergency">
                <div className="section-label">Emergency Contact</div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Full Name</label>
                    <input
                      placeholder="John Smith"
                      value={jsonData.D16}
                      onChange={e => jsonUpdate('D16', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Relationship to Patient</label>
                    <input
                      placeholder="e.g. Spouse, Parent, Sibling"
                      value={jsonData.D17}
                      onChange={e => jsonUpdate('D17', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Primary Phone</label>
                    <input
                      type="tel"
                      placeholder="555-000-0000"
                      value={jsonData.D18}
                      onChange={e => jsonUpdate('D18', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Alternate Phone</label>
                    <input
                      type="tel"
                      placeholder="555-000-0000"
                      value={jsonData.D19}
                      onChange={e => jsonUpdate('D19', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ══ 5. Health Insurance ══════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Health Insurance</div>

                <div className="field mb-14">
                  <label>Health Insurer Name</label>
                  <input placeholder="e.g. Sun Life, Blue Cross, Manulife"
                    value={jsonData.D85} onChange={e => jsonUpdate('D85', e.target.value)} />
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Policy Number</label>
                    <input placeholder="POL-000000"
                      value={jsonData.D86} onChange={e => jsonUpdate('D86', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Group Number</label>
                    <input placeholder="GRP-0000"
                      value={jsonData.D87} onChange={e => jsonUpdate('D87', e.target.value)} />
                  </div>
                </div>

                <div className="field">
                  <label>Relationship to Policyholder</label>
                  <select value={jsonData.D88}
                    onChange={e => jsonUpdate('D88', e.target.value as IntakeJsonData['D88'])}>
                    <option value="">Select…</option>
                    {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* ══ 6. Visit Information ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Visit Information</div>

                <div className="field mb-14">
                  <label>Reason for Today's Visit</label>
                  <textarea placeholder="e.g. Routine eye exam, blurry vision, contact lens fitting…"
                    value={jsonData.D28} onChange={e => jsonUpdate('D28', e.target.value)} />
                </div>
              </div>
                

              {/* ══ 7. Current Vision Symptoms ═══════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Current Vision Symptoms</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>Select all that apply:</p>
                <CheckboxGroup
                  options={VISION_SYMPTOMS}
                  selected={jsonData.D29}
                  onToggle={item => toggleCheck('D29', item)}
                />
              </div>

              {/* ══ 8. Medical History ═══════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Medical History</div>

                {/* Diabetes */}
                <div className="field mb-14">
                  <label>Are you diabetic?</label>
                  <YesNoRadio value={jsonData.D63}
                    onChange={v => jsonUpdate('D63', v as YesNoType)}
                   />
                  {jsonData.D63 === 'Yes' && (
                    <div className="detail-box">
                      <div className="detail-row">
                        <div>
                          <label>Most Recent Blood Sugar (mmol/L)</label>
                          <input placeholder="e.g. 5.6"
                            value={jsonData.D35}
                            onChange={e => jsonUpdate('D35', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pregnancy */}
                <div className="field mb-14">
                  <label>Are you currently pregnant?</label>
                  <YesNoRadio value={jsonData.D62}
                    onChange={v => jsonUpdate('D62', v as YesNoNaType)}
                    includeNA />
                </div>

                {/* Smoker */}
                <div className="field mb-14">
                  <label>Do you smoke?</label>
                  <YesNoRadio value={jsonData.D33}
                    onChange={v => jsonUpdate('D33', v as YesNoType)}
                  />
                </div>

                {/* Eye Surgery */}
                <div className="field mb-14">
                  <label>Have you had any previous eye surgery?</label>
                  <YesNoRadio value={jsonData.hasD24}
                    onChange={v => jsonUpdate('hasD24', v as YesNoType)}
                  />
                  {jsonData.hasD24 === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the surgery (type, date, eye affected)</label>
                      <textarea placeholder="e.g. LASIK on both eyes, 2019…"
                        value={jsonData.D24}
                        onChange={e => jsonUpdate('D24', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Eye Injury */}
                <div className="field mb-14">
                  <label>Have you had any eye injury or trauma?</label>
                  <YesNoRadio value={jsonData.hasD36}
                    onChange={v => jsonUpdate('hasD36', v as YesNoType)}
                  />
                  {jsonData.hasD36 === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the injury</label>
                      <textarea placeholder="e.g. Foreign object in eye, 2021…"
                        value={jsonData.D36}
                        onChange={e => jsonUpdate('D36', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Current Medications */}
                <div className="field mb-14">
                  <label>Current Medications</label>
                  <textarea placeholder="List all prescription and over-the-counter medications, including eye drops…"
                    value={jsonData.D30}
                    onChange={e => jsonUpdate('D30', e.target.value)} />
                </div>

                {/* Allergies */}
                <div className="field mb-14">
                  <label>Known Allergies</label>
                  <input placeholder="e.g. Penicillin, latex, eye drops…"
                    value={jsonData.D31}
                    onChange={e => jsonUpdate('D31', e.target.value)} />
                </div>

                {/* Medical Conditions */}
                <div className="field">
                  <label>
                    Do you have any of the following conditions?{' '}
                    <span style={{ color: '#a0aec0', fontWeight: 400 }}>(select all that apply)</span>
                  </label>
                  <div className="mb-8" />
                  <CheckboxGroup
                    options={MEDICAL_CONDITIONS}
                    selected={jsonData.D25}
                    onToggle={item => toggleCheck('D25', item)}
                  />
             </div>
              </div>

              {/* ══ 9. Family Eye History ════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Eye History</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>
                  Has anyone in your immediate family been diagnosed with:
                </p>
                <CheckboxGroup
                  options={FAMILY_EYE_CONDITIONS}
                  selected={jsonData.D26}
                  onToggle={item => toggleCheck('D26', item)}
                />
              </div>

              {/* ══ 10. Family Physician ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Physician</div>
                <div className="grid-2">
                  <div className="field">
                    <label>Family Doctor's Name</label>
                    <input placeholder="Dr. Name"
                      value={jsonData.D27}
                      onChange={e => jsonUpdate('D27', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Doctor's Phone Number</label>
                    <input type="tel" placeholder="000-000-0000"
                      value={jsonData.D64}
                      onChange={e => jsonUpdate('D64', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ══ 11. Terms & Consent ══════════════════════════════════════════ */}
              <div
                className={['terms-block', form.terms ? 'checked' : '', errors.terms ? 'error-border' : ''].join(' ')}
                onClick={() => update('terms', !form.terms)}
              >
                <div className="custom-check">
                  {form.terms && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="terms-text">
                  I hereby consent to the disclosure of my eye care patient records by my health care
                  provider to {clinicName} for the purpose of fulfilling its obligation with respect to
                  secretarial and administrative support services, including management of patient files,
                  scheduling eye care appointments, issuing reminder notices, telephone answering, fee
                  collection (if applicable), and processing provincial health insurance fee claims (if
                  applicable). <span className="clinic-req">*</span>
                </div>
              </div>

              <div className="recaptcha-container" style={{ marginTop: '16px', marginBottom: '16px' }}>
                <ReCAPTCHA
                  sitekey="6Lc8uG4qAAAAAHCmxZUJINB2c2wH13iuBCWOwr5k" // Replace with your actual Google reCAPTCHA site key
                  onChange={setRecaptchaToken}
                />
              </div>

              {/* ══ Submit ═══════════════════════════════════════════════════════ */}
              <button type="submit" className="submit-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                </svg>
                Submit Intake Form
              </button>

              <div className="privacy-notice">
                🔒 Your information is encrypted and protected under HIPAA / PHIPA.<br />
                This form is for new and returning patients of {clinicName}.
              </div>
            </form>
          )}
        </div>
      </div>
      {toast && (
        <div className="toast-error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default IntakeOnline;